import { InsightEntity } from '../api/types/Insights';
import { getRiskIndicatorByRiskRule } from '@silverfort-nodelibs/common/utils/insight';
import { TableColumn } from '@silverfort/sf-components';
import { bigNumberToText } from '../../../components/utils';
import IdentifierColumn from './columns/IdentifierColumn';
import RiskLevelColumn from './columns/RiskLevelColumn';
import { RISK_LEVELS_OPTIONS } from './Filters/FilterOptions';
import RiskIndicatorsColumn from './columns/RiskIndicatorColumn';
import IDPColumn from './columns/IDPColumn';
import ExtraDataColumn from './columns/ExtraDataColumn';
import { formatExtraDetailsData } from '@silverfort-nodelibs/common/utils/common';
import React, { useEffect, useState } from 'react';
import { StatusColumn } from './columns/StatusColumn';

type InsightTableColumn = TableColumn<InsightEntity> & { extractValue: (row: InsightEntity) => string | string[] };

const useGetColumns = (
    insightIndicator: string,
    insightsDetails: InsightEntity[] = [],
    isInventoryTable: boolean = false,
    onStatusUpdateSuccess?: (newStatusDescription: string) => void,
    onStatusUpdateError?: (newStatusDescription: string) => void
) => {
    const [columns, setColumns] = useState<InsightTableColumn[]>([]);

    useEffect(() => {
        const riskIndicatorExtraColumns = getRiskIndicatorByRiskRule(insightIndicator)?.extraEntityDetails;
        const calculatedColumns: InsightTableColumn[] = [
            {
                columnKey: 'display_name',
                title: `Entities (${bigNumberToText(insightsDetails.length)})`,
                render: (row) => {
                    return <IdentifierColumn {...row} />;
                },
                extractValue: (row) => row?.display_name,
                columnClassName: '[&]:min-w-[308px]'
            },
            {
                columnKey: 'risk_level',
                title: 'Risk level',
                render: RiskLevelColumn,
                extractValue: (row) =>
                    RISK_LEVELS_OPTIONS.find((option) => option.value === row?.riskLevel)?.title ?? ''
            },
            {
                columnKey: 'risk_indicators',
                title: 'Risk indicators',
                render: RiskIndicatorsColumn,
                extractValue: (row) => row?.risk_indicators,
                columnClassName: '[&]:min-w-40'
            }
        ];

        if (!isInventoryTable) {
            calculatedColumns.push({
                columnKey: 'status_description',
                title: 'Status',
                render: (row) => (
                    <StatusColumn
                        entity={row}
                        onSuccess={onStatusUpdateSuccess}
                        onError={onStatusUpdateError}
                    />
                ),
                extractValue: (row) => row?.status,
                columnClassName: 'font-sf [&]:min-w-40'
            });
        }

        calculatedColumns.push({
            columnKey: 'identifier',
            title: 'IdP',
            extractValue: (row) => `azure: ${row.isAzureUser} | ad: ${row.isADUser}`,
            render: (row) => <IDPColumn isADUser={row.isADUser} isAzureUser={row.isAzureUser} />
        });

        riskIndicatorExtraColumns?.forEach((extraDataObj) => {
            calculatedColumns.push({
                // @ts-ignore
                columnKey: `extra_data.${extraDataObj.moreDetailsKey}`,
                title: extraDataObj.moreDetailsHeader,
                render: (row) => <ExtraDataColumn entityData={row} extraDetailsObj={extraDataObj} />,
                extractValue: (row) => formatExtraDetailsData(row, extraDataObj, true).toString(),
                columnClassName: 'font-sf [&]:min-w-50'
            });
        });

        setColumns(calculatedColumns);
    }, [insightIndicator, insightsDetails.length, isInventoryTable]);

    return columns;
};

export default useGetColumns;
